import { useTranslation } from 'react-i18next';
import type { ObserveLocationPersonnelPeriods } from '@apis/reports';
import tagIcon from '@assets/analytics/tag.svg';
import classes from './PersonnelTimeline.module.css';

const DAY_MS = 24 * 3600 * 1000;
// 13 labels span the 24-h axis at every 2-h boundary (12 AM at both ends).
// Rendered as overlay over the 12-bucket cells lane so labels sit exactly at
// the column boundaries — matches trusstor's design.
const HEADER_TICKS = [
  '12 AM', '2 AM', '4 AM', '6 AM', '8 AM', '10 AM',
  '12 PM', '2 PM', '4 PM', '6 PM', '8 PM', '10 PM', '12 AM',
];
// 26 cubes per row, matching trusstor's PeriodGraphRow which renders
// `graphCubes + graphCubes/12 = 24 + 2 = 26` cubes — 24 hour-cells plus
// 2 cushion cubes at the right edge that anchor the "12 AM" closing tick
// label past the last hour bar.
const HOUR_CUBES = Array.from({ length: 26 }, (_, i) => i);

interface IPersonnelTimelineProps {
  personnel: ObserveLocationPersonnelPeriods[];
  isLoading: boolean;
  /** Start-of-day in ms (in tz). Used to position period rects. */
  dayStartMs: number;
}

// Trusstor's period placement uses `oneHourPercent = 100 / (24+2)` so the
// 24-h span maps onto 24 of the 26 cubes — the trailing 2 cubes are
// empty cushion. We mirror that math exactly: each hour = 100/26 ≈ 3.846%
// of the cells lane width, and the period sits at `hours * oneHourPercent`.
const ONE_HOUR_PCT = 100 / 26;
const DAY_HOURS = 24;
// Trusstor's PeriodGraphRow filters out work periods shorter than this
// threshold — they're rendered at width 0, i.e. invisible. The raw
// upstream feed is minute-by-minute presence pings; without this filter
// the row reads as tally marks instead of a clean presence pattern.
// NOTE: trusstor's source comment says "5min" but 0.12 h is actually
// ~7.2 min. We mirror their constant for 1:1 visual parity; if d-orca
// ever owns this threshold independently, drop to 0.083 (5 min exact).
const MIN_PERIOD_HOURS = 0.12;

function pctLeft(startIso: string, dayStartMs: number): number {
  const dt = Math.max(0, new Date(startIso).getTime() - dayStartMs);
  const hours = Math.min(DAY_HOURS, dt / 3_600_000);
  return hours * ONE_HOUR_PCT;
}

/**
 * Returns 0 for periods shorter than MIN_PERIOD_HOURS (≈7.2 min, filtered
 * out — matches trusstor's `getPeriodWidth` which returns `'0px'` for spans
 * below that threshold). Otherwise returns the period's percentage width on
 * the 26-cube axis.
 */
function pctWidth(startIso: string, endIso: string, dayStartMs: number): number {
  const start = Math.max(new Date(startIso).getTime(), dayStartMs);
  const end = Math.min(new Date(endIso).getTime(), dayStartMs + DAY_MS);
  const hours = Math.max(0, (end - start) / 3_600_000);
  if (hours < MIN_PERIOD_HOURS) return 0;
  return hours * ONE_HOUR_PCT;
}

function hoursLabel(totalMs: number): string {
  return `${(totalMs / 3_600_000).toFixed(1)}h`;
}

export function PersonnelTimeline(props: IPersonnelTimelineProps) {
  const { t } = useTranslation();
  const { personnel, isLoading, dayStartMs } = props;

  if (isLoading) {
    return (
      <section className={classes.root} aria-busy="true">
        <h4 className={classes.title}>{t('reports.observeLocation.section.personnel')}</h4>
        <div className={classes.skeleton}>
          <div className={classes.skeletonRow} />
          <div className={classes.skeletonRow} />
          <div className={classes.skeletonRow} />
        </div>
      </section>
    );
  }

  return (
    <section className={classes.root}>
      {/* Unified header — `Personnel` (mainTitle), `Total Time` (subtitle on
          the same axis), and hour ticks share one row with the same
          background as the data rows. Matches trusstor's PeriodsGraph layout
          which has `.header` as a single section with no gray fill. */}
      <div className={classes.headerRow}>
        {/* Left header — 240px column with `Personnel` and `Total Time`
            spaced apart (justify-content: space-between). Matches
            trusstor's `.leftHeader` layout. */}
        <div className={classes.leftHeader}>
          <div className={classes.mainTitle}>
            {t('reports.observeLocation.section.personnel')}
          </div>
          <div className={classes.headerTotal}>
            {t('reports.observeLocation.personnel.totalTime')}
          </div>
        </div>
        <div className={classes.ticksLane}>
          {/* 13 tick labels distributed evenly across the lane via
              flex `justify-content: space-between`. The labels are
              informational; the period bars below carry exact hour
              positioning on the 26-cube grid. */}
          {HEADER_TICKS.map((label, i) => (
            <span key={label + i} className={classes.tick}>
              {label}
            </span>
          ))}
        </div>
      </div>
      {personnel.length === 0 ? (
        <div className={classes.empty}>{t('reports.observeLocation.empty.personnel')}</div>
      ) : null}
      {personnel.map((p) => (
        <div key={p.tagId} className={classes.row}>
          {/* Left row side — 240px column with tag identity on the left
              and total time on the right (justify-content: space-between).
              Matches trusstor's `.leftRowSide`. */}
          <div className={classes.leftRowSide}>
            <div className={classes.who}>
              <div className={classes.avatar}>
                <img src={tagIcon} alt="" className={classes.avatarImg} />
              </div>
              <div className={classes.whoText}>
                {/* Primary line: tagNick (e.g. "19_simulation-eng") — the
                    trusstor convention is to show the tag identity here, with
                    the worker name / trade as the secondary line. */}
                <div className={classes.name}>{p.tagNick || p.name || p.tagId}</div>
                {p.professionTradeId != null ? (
                  <div className={classes.profession}>
                    {/* TODO(useSiteProfessions): render the resolved trade *name* instead of the
                        raw tradeId. The spec's open-question section flagged extracting
                        useSiteProfessions(siteId) — a TanStack hook over a project-professions
                        BFF endpoint — to translate tradeId → name client-side. Until that hook
                        ships, users see the opaque ID (e.g. "tr-elec" instead of "Electrical").
                        Color resolution already works (professionColor is filled adapter-side). */}
                    {p.professionTradeId}
                  </div>
                ) : null}
              </div>
            </div>
            <div className={classes.total}>{hoursLabel(p.totalMs)}</div>
          </div>
          <div className={classes.cells}>
            {/* 24 hour cubes — flex children with right-edge box-shadow draw
                the per-hour grid lines that span the full row height. Matches
                trusstor's PeriodGraphRow `.graphContainer` exactly. The
                cubes don't host content; the period bars below paint on top. */}
            {HOUR_CUBES.map((h) => (
              <div key={`cube-${h}`} className={classes.cube} />
            ))}
            {p.workPeriods.map((wp, i) => (
              <div
                key={i}
                className={classes.period}
                style={{
                  left: `${pctLeft(wp.startIso, dayStartMs).toFixed(4)}%`,
                  width: `${pctWidth(wp.startIso, wp.endIso, dayStartMs).toFixed(4)}%`,
                  // Trade color, resolved adapter-side via listProfessions
                  // and the personnel's `professionTradeId`. Falls back to
                  // the design-system blue when the trade has no color
                  // (utility/manager tags, or unmapped trade IDs).
                  ...(p.professionColor ? { background: p.professionColor } : undefined),
                }}
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
